import { X, Plus } from "lucide-react";
import { Button } from "@/design-system/components/Button/Button";
import { colors, TextField } from "@material-ui/core";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import clsx from "clsx";

import { ConfigureDestinationsModal } from "../../modals/ConfigureDestinationsModal";

import { DestinationConfigRow } from "../shared/ConfigPageRow";
import { useModalStyles } from "../../modals/styles";

const CredentialsModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig,
}) => {
  const settings = existingConfig.settings || {};
  const id = existingConfig.id;

  const limitSettings = ["token", "secret"];

  if (!id) {
    limitSettings.push("preferAnonymousIdForDeviceId");
  }

  return (
    <ConfigureDestinationsModal
      limitSettings={limitSettings}
      open={open}
      includeHeader={true}
      settings={settings}
      dest={destination}
      onClose={onClose}
      onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
      existingConfig={existingConfig}
    />
  );
};

const CredentialsRow = ({ setConfigModalOpen, existingConfig }) => {
  const settings = existingConfig.settings || {};
  const idSet = !!settings.token;

  return (
    <DestinationConfigRow
      name="Credentials"
      textColor={idSet ? "" : colors.red[600]}
      labelText={idSet ? "Configured" : "Not configured"}
      onOpenConfigModal={() => setConfigModalOpen("credentials")}
    />
  );
};

const GroupIdentifierTraitListConfigurationControl = ({
  field,
  value = [],
  onChange,
}) => {
  const classes = useModalStyles();

  const onGroupKeyChange = (index, newGroupKey) => {
    const newValue = value.slice();
    newValue[index] = newGroupKey;
    onChange(field.key, newValue);
  };

  const onDelete = (index) => {
    const newValue = value.slice();
    newValue.splice(index, 1);
    onChange(field.key, newValue);
  };

  return (
    <div className="justify-items-stretch" style={{ width: "100%" }}>
      {value.map((groupKey, index) => {
        return (
          <div className="flex items-stretch" key={index}>
            <GroupIdentifierTraitConfigurationControl
              field={field}
              groupKey={groupKey}
              onGroupKeyChange={(newGroupKey) =>
                onGroupKeyChange(index, newGroupKey)
              }
            />
            <span className="flex flex-wrap justify-end">
              <Tooltip content="Delete Group Identifier Trait">
                <div
                  className="hover:bg-navy-200 flex items-center justify-center rounded-full p-1"
                  onClick={() => onDelete(index)}
                >
                  <X className={classes.closeIcon} />
                </div>
              </Tooltip>
            </span>
          </div>
        );
      })}
      <Button
        onClick={() => onChange(field.key, [...value, { key: "", value: "" }])}
        className="bg-navy-100 text-navy-800 hover:bg-navy-200 inline-flex cursor-pointer items-center rounded px-3 py-1 text-xs"
        trailingIcon={<Plus />}
      >
        Add Group Identifier Trait
      </Button>
    </div>
  );
};

const GroupIdentifierTraitConfigurationControl = ({
  field,
  groupKey,
  onGroupKeyChange,
}) => {
  const classes = useModalStyles();

  const handleGroupKeyChange = (groupKeyChanges) => {
    const newGroupKey = { ...groupKey, ...groupKeyChanges };
    onGroupKeyChange(newGroupKey);
  };

  return (
    <div className="justify-items-stretch" style={{ width: "100%" }}>
      <TextField
        fullWidth
        autoComplete="off"
        label="Group identifier trait"
        name="Group identifier trait"
        onChange={(event) =>
          handleGroupKeyChange({ value: event.target.value })
        }
        value={groupKey.value || ""}
        key={field.name}
        variant="outlined"
        type="text"
        inputProps={{ style: { fontFamily: "monospace" } }}
        className={clsx(classes.inputField, "fs-exclude mb-2")}
      />
    </div>
  );
};

const GroupIdentifierTraitsWrapperControl = ({
  field,
  value = [],
  onChange,
}) => {
  const innerValue = value.map((v) => ({ key: "", value: v }));
  const innerOnChange = (fieldKey, innerValue) => {
    const value = innerValue.map((obj) => obj.value);
    onChange(fieldKey, value);
  };

  return (
    <GroupIdentifierTraitListConfigurationControl
      field={field}
      value={innerValue}
      onChange={innerOnChange}
    />
  );
};

const ConfigurationModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig,
}) => {
  const settings = existingConfig.settings || {};
  const id = existingConfig.id;

  const limitSettings = [
    "groupIdentifierTraits",
    "ip",
    "crossSubdomainCookie",
    "persistence",
    "consolidatedPageCalls",
    "enableEuropeanUnionEndpoint",
    "secureCookie",
    "ignore_dnt",
    "mergeAnonymous",
    "defaultIdentifyTransformations",
  ];

  if (id) {
    limitSettings.push("preferAnonymousIdForDeviceId");
  }

  return (
    <ConfigureDestinationsModal
      settingsToControl={{
        groupIdentifierTraits: GroupIdentifierTraitsWrapperControl,
      }}
      limitSettings={limitSettings}
      open={open}
      settings={settings}
      dest={destination}
      onClose={onClose}
      onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
      existingConfig={existingConfig}
    />
  );
};

const ConfigurationRow = ({ setConfigModalOpen, existingConfig }) => {
  const settings = existingConfig.settings || {};
  const enabled = !!settings.token;

  return (
    <DestinationConfigRow
      disabled={!enabled}
      name="Options"
      labelText=""
      onOpenConfigModal={() => {
        if (enabled) {
          setConfigModalOpen();
        }
      }}
    />
  );
};

const CREDENTIALS = "CREDENTIALS";
const OPTIONS = "OPTIONS";

const Rows = {
  CREDENTIALS: CredentialsRow,
  OPTIONS: ConfigurationRow,
};

const Modals = {
  CREDENTIALS: CredentialsModal,
  OPTIONS: ConfigurationModal,
};

const RowOrder = [CREDENTIALS, OPTIONS];

export default {
  Rows,
  RowOrder,
  Modals,
};
